import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, MessageCircle, Send, Trophy, Award, Star, Calendar, Search, Heart, ThumbsUp } from 'lucide-react';
import { bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format, parseISO, isToday, isFuture } from 'date-fns';

export default function CustomerCommunityPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('discussions'); // 'discussions', 'leaderboard', 'find-classmates'
  const [newPost, setNewPost] = useState('');
  const [searchClass, setSearchClass] = useState('');

  // Fetch user's bookings
  const { data: bookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false
  });

  const myBookings = bookingsData?.data || [];

  // Placeholder data - in real app, this would come from API
  const leaderboardData = [
    { id: 1, name: 'Sarah Johnson', classes: 47, rank: 1, avatar: null },
    { id: 2, name: 'Mike Chen', classes: 42, rank: 2, avatar: null },
    { id: 3, name: 'Emma Williams', classes: 38, rank: 3, avatar: null },
    { id: 4, name: 'David Brown', classes: 35, rank: 4, avatar: null },
    { id: 5, name: 'Lisa Anderson', classes: 32, rank: 5, avatar: null },
    { id: 6, name: user?.firstName + ' ' + user?.lastName, classes: myBookings.filter(b => {
      if (!b.schedule?.specificDate) return false;
      const date = parseISO(b.schedule.specificDate);
      return date < new Date();
    }).length, rank: 6, avatar: null, isCurrentUser: true }
  ].sort((a, b) => b.classes - a.classes).map((item, index) => ({ ...item, rank: index + 1 }));

  const currentUserRank = leaderboardData.find(u => u.isCurrentUser)?.rank || leaderboardData.length + 1;
  const currentUserClasses = myBookings.filter(b => {
    if (!b.schedule?.specificDate) return false;
    const date = parseISO(b.schedule.specificDate);
    return date < new Date();
  }).length;

  // Placeholder discussions - in real app, this would come from API
  const [discussions, setDiscussions] = useState([
    {
      id: 1,
      author: 'Sarah Johnson',
      content: 'Just finished an amazing yoga session! Who else is going to the evening class tomorrow?',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      likes: 12,
      replies: 5,
      classMention: 'Yoga Evening Class'
    },
    {
      id: 2,
      author: 'Mike Chen',
      content: 'Looking for a workout buddy for the fitness class this weekend. Anyone interested?',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
      likes: 8,
      replies: 3,
      classMention: 'Fitness Weekend'
    },
    {
      id: 3,
      author: 'Emma Williams',
      content: 'The meditation class was so relaxing! Highly recommend it to everyone.',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      likes: 15,
      replies: 7,
      classMention: 'Meditation Class'
    }
  ]);

  const handlePostDiscussion = () => {
    if (!newPost.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    const newDiscussion = {
      id: discussions.length + 1,
      author: `${user?.firstName} ${user?.lastName}`,
      content: newPost,
      timestamp: new Date(),
      likes: 0,
      replies: 0,
      classMention: null
    };
    
    setDiscussions([newDiscussion, ...discussions]);
    setNewPost('');
    toast.success('Post shared!');
  };

  const handleLike = (id) => {
    setDiscussions(discussions.map(d => 
      d.id === id ? { ...d, likes: d.likes + 1 } : d
    ));
  };

  // Get upcoming classes for finding classmates
  const upcomingClasses = myBookings
    .filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const date = parseISO(booking.schedule.specificDate);
      return isFuture(date) || isToday(date);
    })
    .map(booking => ({
      ...booking,
      classDate: parseISO(booking.schedule.specificDate),
      className: booking.schedule?.video?.title || 'Class'
    }))
    .sort((a, b) => a.classDate - b.classDate);

  // Get rank badge/icon
  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-100', label: '🥇 Champion' };
    if (rank === 2) return { icon: Award, color: 'text-gray-400', bg: 'bg-gray-100', label: '🥈 Runner-up' };
    if (rank === 3) return { icon: Award, color: 'text-orange-500', bg: 'bg-orange-100', label: '🥉 Third Place' };
    if (rank <= 10) return { icon: Star, color: 'text-purple-500', bg: 'bg-purple-100', label: '⭐ Top 10' };
    if (rank <= 25) return { icon: Star, color: 'text-blue-500', bg: 'bg-blue-100', label: '⭐ Top 25' };
    return { icon: null, color: 'text-[var(--text-secondary)]', bg: 'bg-primary-100', label: `#${rank}` };
  };

  // Get prize for rank
  const getPrize = (rank, classes) => {
    if (rank === 1 && classes >= 50) return '🏆 Grand Champion - Free 3-month subscription + Studio merchandise!';
    if (rank === 1) return '🏆 Champion - Free 1-month subscription!';
    if (rank === 2) return '🥈 Runner-up - 50% off next month!';
    if (rank === 3) return '🥉 Third Place - 25% off next month!';
    if (rank <= 10 && classes >= 30) return '⭐ Top 10 - 15% off next month!';
    if (classes >= 25) return '🎁 Milestone - 10% off next class!';
    if (classes >= 10) return '🎉 Achievement - Keep it up!';
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">Community</h1>
        <p className="text-[var(--text-secondary)] mt-2">Connect with fellow members, share experiences, and find workout buddies!</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--border-subtle)]">
        <button
          onClick={() => setActiveTab('discussions')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'discussions'
              ? 'text-[var(--text-secondary)] border-b-2 border-primary-600'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Discussions
        </button>
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'leaderboard'
              ? 'text-[var(--text-secondary)] border-b-2 border-primary-600'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <Trophy className="w-4 h-4" />
          Leaderboard
        </button>
        <button
          onClick={() => setActiveTab('find-classmates')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'find-classmates'
              ? 'text-[var(--text-secondary)] border-b-2 border-primary-600'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <Users className="w-4 h-4" />
          Find Classmates
        </button>
      </div>

      {/* Discussions Tab */}
      {activeTab === 'discussions' && (
        <div className="space-y-6">
          {/* Create Post */}
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Share Your Thoughts</h3>
            <div className="space-y-3">
              <textarea
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="What's on your mind? Share your class experience, ask questions, or find workout buddies..."
                className="w-full p-3 border border-[var(--border-subtle)] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={4}
              />
              <button
                onClick={handlePostDiscussion}
                className="btn btn-primary flex items-center gap-2 ml-auto shadow-md hover:shadow-lg transition-all duration-300"
              >
                <Send className="w-4 h-4" />
                Post
              </button>
            </div>
          </div>

          {/* Discussions Feed */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-[var(--text-primary)]">Community Discussions</h3>
            {discussions.map((discussion) => (
              <div key={discussion.id} className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[var(--text-secondary)]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-[var(--text-primary)]">{discussion.author}</h4>
                      <span className="text-xs text-[var(--text-muted)]">
                        {format(discussion.timestamp, 'MMM d, h:mm a')}
                      </span>
                      {discussion.classMention && (
                        <span className="text-xs bg-primary-100 text-[var(--text-secondary)] px-2 py-1 rounded">
                          {discussion.classMention}
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--text-secondary)] mb-3">{discussion.content}</p>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleLike(discussion.id)}
                        className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-secondary)] transition-colors"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>{discussion.likes}</span>
                      </button>
                      <button className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-secondary)] transition-colors">
                        <MessageCircle className="w-4 h-4" />
                        <span>{discussion.replies} replies</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="space-y-6">
          {/* Current User Stats */}
          <div className="card bg-gradient-to-br from-primary-50 to-accent-50 border-2 border-[var(--border-subtle)] hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">Your Ranking</h3>
                <div className="flex items-center gap-3">
                  {(() => {
                    const badge = getRankBadge(currentUserRank);
                    const BadgeIcon = badge.icon;
                    return (
                      <>
                        {BadgeIcon && (
                          <div className={`w-12 h-12 ${badge.bg} rounded-full flex items-center justify-center`}>
                            <BadgeIcon className={`w-6 h-6 ${badge.color}`} />
                          </div>
                        )}
                        <div>
                          <p className="text-3xl font-bold text-[var(--text-primary)]">{badge.label}</p>
                          <p className="text-[var(--text-secondary)]">You've completed {currentUserClasses} classes</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              {getPrize(currentUserRank, currentUserClasses) && (
                <div className="text-right">
                  <p className="text-sm text-[var(--text-secondary)] mb-1">Your Prize:</p>
                  <p className="text-lg font-bold text-[var(--text-primary)]">{getPrize(currentUserRank, currentUserClasses)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard */}
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
            <h3 className="text-xl font-bold text-[var(--text-primary)] mb-4">Top Performers</h3>
            <div className="space-y-3">
              {leaderboardData.slice(0, 10).map((member) => {
                const badge = getRankBadge(member.rank);
                const BadgeIcon = badge.icon;
                const prize = getPrize(member.rank, member.classes);
                
                return (
                  <div
                    key={member.id}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 transform hover:-translate-y-0.5 ${
                      member.isCurrentUser
                        ? 'border-primary-300 shadow-md hover:shadow-lg'
                        : 'border-[var(--border-subtle)] hover:shadow-lg hover:border-[var(--border-subtle)]'
                    }`}
                    style={{ background: member.isCurrentUser ? 'var(--primary-10)' : 'var(--bg-elevated)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12">
                          {BadgeIcon ? (
                            <div className={`w-10 h-10 ${badge.bg} rounded-full flex items-center justify-center`}>
                              <BadgeIcon className={`w-6 h-6 ${badge.color}`} />
                            </div>
                          ) : (
                            <span className={`text-lg font-bold ${badge.color}`}>#{member.rank}</span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-[var(--text-primary)]">
                              {member.name}
                              {member.isCurrentUser && <span className="text-[var(--text-secondary)] ml-2">(You)</span>}
                            </h4>
                          </div>
                          <p className="text-sm text-[var(--text-secondary)]">{member.classes} classes completed</p>
                        </div>
                      </div>
                      {prize && (
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[var(--text-secondary)]">{prize}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Prize Information */}
            <div className="mt-6 pt-6 border-t border-[var(--border-subtle)]">
              <h4 className="font-semibold text-[var(--text-primary)] mb-3">🏆 Prize Tiers</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <p className="font-semibold text-yellow-900">🥇 #1 (50+ classes)</p>
                  <p className="text-yellow-700">Free 3-month subscription + Studio merchandise!</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900">🥈 #2</p>
                  <p className="text-gray-700">50% off next month!</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="font-semibold text-orange-900">🥉 #3</p>
                  <p className="text-orange-700">25% off next month!</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="font-semibold text-purple-900">⭐ Top 10 (30+ classes)</p>
                  <p className="text-purple-700">15% off next month!</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="font-semibold text-blue-900">🎁 25+ Classes</p>
                  <p className="text-blue-700">10% off next class!</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="font-semibold text-green-900">🎉 10+ Classes</p>
                  <p className="text-green-700">Achievement unlocked!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Find Classmates Tab */}
      {activeTab === 'find-classmates' && (
        <div className="space-y-6">
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Find People Going to Your Classes</h3>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
              <input
                type="text"
                value={searchClass}
                onChange={(e) => setSearchClass(e.target.value)}
                placeholder="Search for a class..."
                className="w-full pl-10 pr-4 py-2 border border-[var(--border-subtle)] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {upcomingClasses.length === 0 ? (
            <div className="card text-center py-12">
              <Calendar className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">No Upcoming Classes</h3>
              <p className="text-[var(--text-secondary)]">Book a class to find classmates!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingClasses
                .filter(booking => {
                  if (!searchClass) return true;
                  return booking.className.toLowerCase().includes(searchClass.toLowerCase());
                })
                .map((booking) => (
                  <div key={booking.id} className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-[var(--text-primary)] mb-2 group-hover:text-[var(--text-secondary)] transition-colors">{booking.className}</h4>
                        <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)] mb-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{format(booking.classDate, 'EEEE, MMMM d, yyyy')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>Looking for classmates...</span>
                          </div>
                        </div>
                        {/* Placeholder for classmates - in real app, this would show actual classmates */}
                        <div className="bg-primary-50 p-3 rounded-lg">
                          <p className="text-sm text-[var(--text-secondary)]">
                            💬 <strong>3 people</strong> are going to this class. Connect with them in the discussions!
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

